import { PURPOSE_NAME_BY_VALVE_TYPE } from '../HydraulicRule/FlowRulePanel/valveDecoderOptions'

/**
 * SCRUM-1032 — what a decoder's purpose change would break.
 *
 * A decoder's purpose is not only a label: a station holds an irrigation or
 * non-irrigation decoder, and a flow valve holds a master valve, pump start or
 * booster pump one. Change the purpose out from under either and the record
 * that points at it is left holding a decoder that no longer does its job —
 * silently, and only discovered later when a picker refuses to offer it.
 *
 * So the change is checked before it is staged. What comes back is the list of
 * records that would be left invalid, which the wizard puts to the user as a
 * choice: keep the purpose as it was, or change it and remove them.
 *
 * The two changes that are NOT breakages, and are deliberately absent here:
 * irrigation ⇄ non-irrigation on a station (the station stays valid; only its
 * Type is stale, and the station list flags that), and any change on a decoder
 * nothing points at.
 */

const normalise = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

const STATION_PURPOSES = new Set(['irrigation', 'nonirrigation'].map(normalise))

/** `address-index` — how every endpoint here names a decoder. */
export const decoderKey = (decoder: {
  address?: number | string
  index?: number | string
}) => `${decoder?.address}-${decoder?.index}`

export interface DependentStation {
  id: number | string
  description?: string
}

export interface DependentValve {
  id: number | string
  description?: string
  /** 0 pump, 3 booster pump, 1 and 2 the two master valve wirings. */
  type?: number
}

export interface PurposeChangeImpact {
  stations: DependentStation[]
  valves: DependentValve[]
}

export const NO_IMPACT: PurposeChangeImpact = { stations: [], valves: [] }

export const hasImpact = (impact: PurposeChangeImpact) =>
  impact.stations.length > 0 || impact.valves.length > 0

interface ImpactArgs {
  /** The decoder whose purpose is being changed. */
  decoder: {
    id?: string
    address?: number | string
    index?: number | string
    twiId?: number | null
  }
  /** The purpose it would become, by name. */
  nextPurposeName: string
  /** Every station on the controller (needs `address`/`index` or `decoderAddress`). */
  stations: any[]
  /** Every flow valve and pump on the controller. */
  valves: any[]
}

const addressOf = (record: any): string =>
  String(
    record?.decoderAddress ??
      (record?.address !== undefined ? decoderKey(record) : ''),
  )

/**
 * The records this purpose change would leave invalid.
 *
 * A station breaks when the new purpose is neither irrigation nor
 * non-irrigation; a valve breaks when the new purpose is not the one its own
 * type calls for (a pump row needs a Pump Start decoder, and so on).
 */
export const purposeChangeImpact = ({
  decoder,
  nextPurposeName,
  stations,
  valves,
}: ImpactArgs): PurposeChangeImpact => {
  const key = decoderKey(decoder)
  if (!key || key.includes('undefined')) return NO_IMPACT

  // An empty purpose (a cleared cell, or a fill-down from an empty one) is a
  // change like any other, and the worst kind: no station or valve can hold a
  // decoder with no purpose, so every record on this decoder breaks. It used to
  // answer "no impact" here, and a fill that cleared purposes orphaned them
  // without a word.
  const next = normalise(nextPurposeName)

  // `Array.isArray`, not `?? []`: an empty list comes back from the station
  // hook as `false` (`data.data.length > 0 && map(...)`), which `??` lets
  // through — and `false.filter` threw out of the grid's commit handler, so no
  // purpose edit landed at all on a controller with no stations yet.
  const stationRows = Array.isArray(stations) ? stations : []
  const valveRows = Array.isArray(valves) ? valves : []

  const isOnDecoder = (record: any) => {
    if (record?.decoderId && decoder?.id)
      return String(record.decoderId) === String(decoder.id)
    if (addressOf(record) !== key) return false
    return (
      record?.twiId == null ||
      decoder?.twiId == null ||
      Number(record.twiId) === Number(decoder.twiId)
    )
  }

  const brokenStations = STATION_PURPOSES.has(next)
    ? []
    : stationRows.filter(isOnDecoder)

  const brokenValves = valveRows.filter((valve) => {
    if (!isOnDecoder(valve)) return false
    const required = PURPOSE_NAME_BY_VALVE_TYPE[Number(valve?.type)]
    // A valve with a type this app does not know is left alone rather than
    // declared broken on a guess.
    if (!required) return false
    return normalise(required) !== next
  })

  return {
    stations: brokenStations.map((station) => ({
      id: station.id,
      description: station.description ?? station.stationName,
    })),
    valves: brokenValves.map((valve) => ({
      id: valve.id,
      description: valve.description,
      type: Number(valve.type),
    })),
  }
}

export interface ImpactCounts {
  stations: number
  valves: number
  pumps: number
}

/**
 * What would go, counted by kind — the numbers behind "2 stations and 1 pump".
 *
 * Only the counting lives here. The sentence is assembled by whoever renders
 * it, from whole translated phrases: "2 stations" is not a number glued to a
 * word in every language, and a count that reads as one part of speech in
 * English can need another once it is joined to the rest.
 */
export const impactCounts = (impact: PurposeChangeImpact): ImpactCounts => {
  const pumps = impact.valves.filter(
    (valve) => Number(valve.type) === 0 || Number(valve.type) === 3,
  ).length

  return {
    stations: impact.stations.length,
    valves: impact.valves.length - pumps,
    pumps,
  }
}

/**
 * One decoder's pending purpose change, held back until the user answers the
 * dependents dialog.
 */
export interface PurposePromptEntry<Row = unknown> {
  rowId: string
  row: Row
  decoderAddress: string
  fromPurpose: string
  toPurpose: string
  impact: PurposeChangeImpact
}

/**
 * Adds a decoder to the open prompt rather than replacing it.
 *
 * A fill or paste across several rows asks about each row in turn, in the same
 * tick. When each one replaced the prompt, only the last row was put to the
 * user and the rest were silently rejected. A second ask about a row already
 * held replaces that row's entry.
 */
export const addPurposePrompt = <Row>(
  entries: PurposePromptEntry<Row>[],
  entry: PurposePromptEntry<Row>,
): PurposePromptEntry<Row>[] => {
  const at = entries.findIndex((held) => held.rowId === entry.rowId)
  if (at === -1) return [...entries, entry]
  const next = [...entries]
  next[at] = entry
  return next
}

const uniqueById = <T extends { id: number | string }>(items: T[]) => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = String(item.id)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Everything the held changes would remove, each record once. */
export const combinedImpact = (
  entries: PurposePromptEntry<unknown>[],
): PurposeChangeImpact => ({
  stations: uniqueById(entries.flatMap((entry) => entry.impact.stations)),
  valves: uniqueById(entries.flatMap((entry) => entry.impact.valves)),
})

/**
 * The held changes once some dependents are gone — after a removal that failed
 * part way. What was deleted must not be offered (and deleted, and 404) again.
 */
export const withoutRemoved = <Row>(
  entries: PurposePromptEntry<Row>[],
  removed: { stations: Array<number | string>; valves: Array<number | string> },
): PurposePromptEntry<Row>[] => {
  const stations = new Set(removed.stations.map(String))
  const valves = new Set(removed.valves.map(String))
  return entries.map((entry) => ({
    ...entry,
    impact: {
      stations: entry.impact.stations.filter((s) => !stations.has(String(s.id))),
      valves: entry.impact.valves.filter((v) => !valves.has(String(v.id))),
    },
  }))
}

/**
 * Deletes the dependents — valves first, then stations, one at a time (these
 * endpoints answer 500 under concurrent writes) — and reports what went, even
 * when a delete fails part way.
 */
export const removeDependents = async (
  impact: PurposeChangeImpact,
  remove: {
    valve: (id: number | string) => Promise<unknown>
    station: (id: number | string) => Promise<unknown>
  },
): Promise<{
  removed: { stations: Array<number | string>; valves: Array<number | string> }
  error: unknown | null
}> => {
  const removed = { stations: [] as Array<number | string>, valves: [] as Array<number | string> }
  try {
    for (const valve of impact.valves) {
      await remove.valve(valve.id)
      removed.valves.push(valve.id)
    }
    for (const station of impact.stations) {
      await remove.station(station.id)
      removed.stations.push(station.id)
    }
    return { removed, error: null }
  } catch (error) {
    return { removed, error }
  }
}
